import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import {
  Box, Button, TextField, Grid, Typography, Paper,
  Divider, FormControlLabel, Checkbox, CircularProgress, MenuItem
} from '@mui/material';
import { Client, ServicesScope, EmailCampaignThresholds, EmailCampaignScoring, CampaignBudgets } from '../../types/client';
import apiService from '../../services/api';
import FormFillCustomFieldsBuilder from './FormFillCustomFieldsBuilder';

interface ClientFormProps {
  client?: Client;
  onSave: () => void;
  onCancel: () => void;
}

// Service label mapping for proper display
const getServiceLabel = (key: string): string => {
  const labelMap: Record<string, string> = {
    // Content services
    'brochures_datasheets': 'Brochures/Datasheets/Flyers/Playbooks/Case Studies',
    'whitepapers_ebooks': 'Whitepapers/eBooks/Guides',
    'videos': 'Videos - Promo, Explainer, Demo, Podcast, Testimonials',
    // Branding services
    'brand_identity': 'Brand Identity Documents',
    'brand_mascot': 'Brand Mascot/Character',
    // Events services
    'pre_event_marketing': 'Pre-Event Marketing (social, emails, telemarketing, ads)',
    'event_booth_support': 'Event Booth/Content Branding Support',
    'post_event_content': 'Post-Event Content & Reporting',
    'post_event_outreach': 'Post-Event Outreach (emails, telemarketing)'
  };
  
  return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Default services scope
const defaultServicesScope: ServicesScope = {
  organic_social_media: {
    linkedin: false,
    instagram: false,
    facebook: false,
    youtube: false,
    x: false,
    others: false
  },
  paid_marketing: {
    meta: false,
    linkedin_ads: false,
    google_ads: false
  },
  content: {
    social_media_posts: false,
    blogs: false,
    landing_pages: false,
    emails: false,
    brochures_datasheets: false,
    whitepapers_ebooks: false,
    videos: false,
    video_shoots: false,
    sales_decks: false,
    investor_decks: false
  },
  seo: false,
  website_development: false,
  email_marketing: false,
  telemarketing: false,
  branding: {
    logo: false,
    brand_identity: false,
    brand_names: false,
    taglines: false,
    branding_guidelines: false,
    brand_mascot: false
  },
  events: {
    pre_event_marketing: false,
    event_booth_support: false,
    post_event_content: false,
    post_event_outreach: false
  }
};

// Default email campaign thresholds
const defaultEmailCampaignThresholds: EmailCampaignThresholds = {
  mofu: 0
};

// Default email campaign scoring
const defaultEmailCampaignScoring: EmailCampaignScoring = {
  opened: 5,
  clicked: 10,
  unsubscribed: -75,
  spam: -75,
  bounced: -75
};

// Default budget configuration
const defaultCampaignBudgets: CampaignBudgets = {
  email_campaigns: 0,
  telemarketing: 0,
  linkedin: 0,
  google: 0,
  meta: 0,
  currency: 'INR'
};

// New client template
const newClientTemplate: Client = {
  client_name: '',
  account: '',
  client_url: '',
  contract_start_date: undefined,
  contract_end_date: undefined,
  client_point_of_contact: {
    name: '',
    email: '',
    contact_number: ''
  },
  wizrdom_point_of_contact: {
    name: '',
    email: '',
    contact_number: ''
  },
  services_scope: { ...defaultServicesScope },
  email_campaign_thresholds: { ...defaultEmailCampaignThresholds },
  email_campaign_scoring: { ...defaultEmailCampaignScoring },
  campaign_budgets: { ...defaultCampaignBudgets },
  form_fill_custom_fields: []
};

const ClientForm: React.FC<ClientFormProps> = ({ client, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Client>(client || { ...newClientTemplate });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  
  // Reset form when client prop changes
  useEffect(() => {
    if (client) {
      // Ensure all optional fields have proper defaults for form display
      const formClient: Client = {
        ...client,
        client_name: client.client_name || '',
        client_url: client.client_url || '',
        contract_start_date: client.contract_start_date || undefined,
        contract_end_date: client.contract_end_date || undefined,
        client_point_of_contact: client.client_point_of_contact || {
          name: '',
          email: '',
          contact_number: ''
        },
        wizrdom_point_of_contact: client.wizrdom_point_of_contact || {
          name: '',
          email: '',
          contact_number: ''
        },
        services_scope: client.services_scope || { ...defaultServicesScope },
        email_campaign_thresholds: client.email_campaign_thresholds || { ...defaultEmailCampaignThresholds },
        email_campaign_scoring: client.email_campaign_scoring || { ...defaultEmailCampaignScoring },
        campaign_budgets: client.campaign_budgets || { ...defaultCampaignBudgets },
        form_fill_custom_fields: client.form_fill_custom_fields || []
      };
      setFormData(formClient);
    } else {
      setFormData({ ...newClientTemplate });
    }
    setErrors({});
  }, [client]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (!name) return;

    // Handle nested properties
    if (name.includes('.')) {
      const parts = name.split('.');
      setFormData(prevData => {
        // Create a deep copy of the object
        const newData = { ...prevData };
        
        // Navigate to the correct nested property and update it
        let current: any = newData;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }
        
        // Set the value at the final property
        current[parts[parts.length - 1]] = value;
        
        return newData;
      });
    } else {
      // Handle top-level properties
      setFormData(prevData => ({
        ...prevData,
        [name]: value
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (!name) return;

    setFormData(prevData => {
      const newData = { ...prevData };
      
      // Handle nested services_scope checkboxes
      if (name.startsWith('services_scope.')) {
        const parts = name.split('.');
        if (!newData.services_scope) {
          newData.services_scope = { ...defaultServicesScope };
        }
        
        // Handle nested objects (organic_social_media, paid_marketing, etc.)
        if (parts.length === 3) {
          const section = parts[1] as keyof ServicesScope;
          const field = parts[2];
          
          // @ts-ignore
          if (typeof newData.services_scope[section] === 'object') {
            newData.services_scope = {
              ...newData.services_scope,
              [section]: {
                // @ts-ignore
                ...newData.services_scope[section],
                [field]: checked
              }
            };
          }
        } else if (parts.length === 2) {
          // Handle top-level boolean fields (seo, website_development, etc.)
          const field = parts[1] as keyof ServicesScope;
          newData.services_scope = {
            ...newData.services_scope,
            [field]: checked
          };
        }
      }
      
      return newData;
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Basic validation - client_name is optional per backend
    if (!formData.account) newErrors['account'] = 'Account is required';
    
    // For new clients, only validate basic fields
    if (!client) {
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }
    
    // For existing clients, validate all fields
    // Website URL optional; basic domain pattern (allow starting with www. or bare domain); no scheme required
    if (formData.client_url) {
      const domainPattern = /^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/.*)?$/; // simple domain.tld[/path]
      const cleaned = formData.client_url.replace(/^https?:\/\//i, '');
      if (!domainPattern.test(cleaned)) {
        newErrors['client_url'] = 'Enter a valid domain like www.example.com or example.com';
      }
    }

    // Client point of contact optional; basic email format if provided
    if (formData.client_point_of_contact?.email && !/^\S+@\S+\.\S+$/.test(formData.client_point_of_contact.email)) {
      newErrors['client_point_of_contact.email'] = 'Invalid email format';
    }

    // Wizrdom point of contact optional; basic email format if provided
    if (formData.wizrdom_point_of_contact?.email && !/^\S+@\S+\.\S+$/.test(formData.wizrdom_point_of_contact.email)) {
      newErrors['wizrdom_point_of_contact.email'] = 'Invalid email format';
    }

    // Require at least one service selected
    const hasAnyService = formData.services_scope && (
      Object.values(formData.services_scope.organic_social_media || {}).some(v => v) ||
      Object.values(formData.services_scope.paid_marketing || {}).some(v => v) ||
      Object.values(formData.services_scope.content || {}).some(v => v) ||
      formData.services_scope.seo ||
      formData.services_scope.website_development ||
      formData.services_scope.email_marketing ||
      formData.services_scope.telemarketing ||
      Object.values(formData.services_scope.branding || {}).some(v => v) ||
      Object.values(formData.services_scope.events || {}).some(v => v)
    );
    
    if (!hasAnyService) {
      newErrors['services_scope'] = 'Select at least one service';
    }

    // Email Campaign Thresholds required
    if (formData.email_campaign_thresholds?.mofu === undefined || formData.email_campaign_thresholds?.mofu === null) {
      newErrors['email_campaign_thresholds.mofu'] = 'Required';
    }

    // Email Campaign Scoring weights required (can be negative)
    const s = formData.email_campaign_scoring;
    if (s) {
      ['opened','clicked','unsubscribed','spam','bounced'].forEach(k => {
        // @ts-ignore
        if (s[k] === undefined || s[k] === null) newErrors[`email_campaign_scoring.${k}`] = 'Required';
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.debug('ClientForm: handleSubmit called');
    logger.debug('ClientForm: formData:', formData);
    logger.debug('ClientForm: client prop:', client);
    
    if (!validateForm()) {
      logger.debug('ClientForm: validation failed');
      return;
    }
    
    logger.debug('ClientForm: validation passed, submitting...');
    setSubmitting(true);
    try {
      // Clean up the data before sending
      const cleanedData = {
        ...formData,
        client_name: formData.client_name?.trim() || undefined,
        client_url: formData.client_url?.trim() || undefined,
        contract_start_date: formData.contract_start_date?.trim() || undefined,
        contract_end_date: formData.contract_end_date?.trim() || undefined,
        client_point_of_contact: formData.client_point_of_contact && (
          formData.client_point_of_contact.name?.trim() || 
          formData.client_point_of_contact.email?.trim() || 
          formData.client_point_of_contact.contact_number?.trim()
        ) ? {
          name: formData.client_point_of_contact.name?.trim() || undefined,
          email: formData.client_point_of_contact.email?.trim() || undefined,
          contact_number: formData.client_point_of_contact.contact_number?.trim() || undefined
        } : undefined,
        wizrdom_point_of_contact: formData.wizrdom_point_of_contact && (
          formData.wizrdom_point_of_contact.name?.trim() || 
          formData.wizrdom_point_of_contact.email?.trim() || 
          formData.wizrdom_point_of_contact.contact_number?.trim()
        ) ? {
          name: formData.wizrdom_point_of_contact.name?.trim() || undefined,
          email: formData.wizrdom_point_of_contact.email?.trim() || undefined,
          contact_number: formData.wizrdom_point_of_contact.contact_number?.trim() || undefined
        } : undefined
      };

      logger.debug('ClientForm: cleanedData:', cleanedData);

      if (client) {
        // Update existing client
        logger.debug('ClientForm: updating existing client');
        await apiService.updateClient(formData.account, cleanedData);
      } else {
        // Create new client
        logger.debug('ClientForm: creating new client');
        await apiService.createClient(cleanedData);
      }
      
      logger.debug('ClientForm: API call successful, calling onSave');
      onSave();
    } catch (err: any) {
      logger.error('ClientForm: Error saving client:', err);
      
      // Handle different error types
      let errorMessage = 'Failed to save client. Please try again later.';
      
      if (err.response) {
        // HTTP error response received
        logger.error('ClientForm: HTTP error response:', err.response.status, err.response.data);
        if (err.response.status === 401 || err.response.status === 403) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.response.status === 409) {
          errorMessage = 'A client with this account ID already exists.';
        } else if (err.response.status === 400) {
          errorMessage = 'Invalid data submitted. Please check the form.';
        } else if (err.response.data && err.response.data.detail) {
          errorMessage = `Error: ${err.response.data.detail}`;
        }
      } else if (err.request) {
        // Request was made but no response received (network error)
        logger.error('ClientForm: Network error - no response received');
        errorMessage = 'Network error. Please check your connection.';
      }
      
      // Show global error
      logger.debug('ClientForm: Setting error message:', errorMessage);
      setErrors(prev => ({ 
        ...prev, 
        global: errorMessage
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <form onSubmit={handleSubmit}>
        <Typography variant="h5" gutterBottom>
          {client ? 'Edit Client' : 'Add New Client'}
        </Typography>
        
        {errors.global && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errors.global}
          </Typography>
        )}
        
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6">Basic Information</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Client Name"
              name="client_name"
              value={formData.client_name}
              onChange={handleChange}
              error={!!errors['client_name']}
              helperText={errors['client_name'] || 'Optional display name'}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Account"
              name="account"
              value={formData.account}
              onChange={handleChange}
              error={!!errors['account']}
              helperText={errors['account'] || 'Required unique identifier'}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Website URL"
              name="client_url"
              value={formData.client_url || ''}
              onChange={handleChange}
              error={!!errors['client_url']}
              helperText={errors['client_url']}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contract Start Date"
              type="date"
              name="contract_start_date"
              value={formData.contract_start_date || ''}
              onChange={handleChange}
              error={!!errors['contract_start_date']}
              helperText={errors['contract_start_date'] || 'Contract start date'}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contract End Date"
              type="date"
              name="contract_end_date"
              value={formData.contract_end_date || ''}
              onChange={handleChange}
              error={!!errors['contract_end_date']}
              helperText={errors['contract_end_date'] || 'Contract end date'}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Client Point of Contact */}
          <Grid item xs={12}>
            <Typography variant="h6">Client Point of Contact</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Name"
              name="client_point_of_contact.name"
              value={formData.client_point_of_contact?.name || ''}
              onChange={handleChange}
              error={!!errors['client_point_of_contact.name']}
              helperText={errors['client_point_of_contact.name']}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Email"
              name="client_point_of_contact.email"
              value={formData.client_point_of_contact?.email || ''}
              onChange={handleChange}
              error={!!errors['client_point_of_contact.email']}
              helperText={errors['client_point_of_contact.email']}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Phone"
              name="client_point_of_contact.contact_number"
              value={formData.client_point_of_contact?.contact_number || ''}
              onChange={handleChange}
              error={!!errors['client_point_of_contact.contact_number']}
              helperText={errors['client_point_of_contact.contact_number']}
            />
          </Grid>

          {/* Wizrdom Point of Contact */}
          <Grid item xs={12}>
            <Typography variant="h6">Wizrdom Point of Contact</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Name"
              name="wizrdom_point_of_contact.name"
              value={formData.wizrdom_point_of_contact?.name || ''}
              onChange={handleChange}
              error={!!errors['wizrdom_point_of_contact.name']}
              helperText={errors['wizrdom_point_of_contact.name']}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Email"
              name="wizrdom_point_of_contact.email"
              value={formData.wizrdom_point_of_contact?.email || ''}
              onChange={handleChange}
              error={!!errors['wizrdom_point_of_contact.email']}
              helperText={errors['wizrdom_point_of_contact.email']}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Phone"
              name="wizrdom_point_of_contact.contact_number"
              value={formData.wizrdom_point_of_contact?.contact_number || ''}
              onChange={handleChange}
              error={!!errors['wizrdom_point_of_contact.contact_number']}
              helperText={errors['wizrdom_point_of_contact.contact_number']}
            />
          </Grid>

          {/* Services Scope */}
          <Grid item xs={12}>
            <Typography variant="h6">Services Scope</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          {/* Organic Social Media (SMO) */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
              Organic Social Media (SMO)
            </Typography>
            <Grid container spacing={2}>
              {formData.services_scope?.organic_social_media && Object.entries(formData.services_scope.organic_social_media).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!value}
                        onChange={handleCheckboxChange}
                        name={`services_scope.organic_social_media.${key}`}
                      />
                    }
                    label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Paid Marketing */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
              Paid Marketing
            </Typography>
            <Grid container spacing={2}>
              {formData.services_scope?.paid_marketing && Object.entries(formData.services_scope.paid_marketing).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!value}
                        onChange={handleCheckboxChange}
                        name={`services_scope.paid_marketing.${key}`}
                      />
                    }
                    label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Content Services */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
              Content
            </Typography>
            <Grid container spacing={2}>
              {formData.services_scope?.content && Object.entries(formData.services_scope.content).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!value}
                        onChange={handleCheckboxChange}
                        name={`services_scope.content.${key}`}
                      />
                    }
                    label={getServiceLabel(key)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* SEO, Website Development, Email Marketing, Telemarketing */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
              Other Services
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.services_scope?.seo}
                      onChange={handleCheckboxChange}
                      name="services_scope.seo"
                    />
                  }
                  label="SEO"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.services_scope?.website_development}
                      onChange={handleCheckboxChange}
                      name="services_scope.website_development"
                    />
                  }
                  label="Website Development"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.services_scope?.email_marketing}
                      onChange={handleCheckboxChange}
                      name="services_scope.email_marketing"
                    />
                  }
                  label="Email Marketing"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.services_scope?.telemarketing}
                      onChange={handleCheckboxChange}
                      name="services_scope.telemarketing"
                    />
                  }
                  label="Telemarketing"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Branding Services */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
              Branding
            </Typography>
            <Grid container spacing={2}>
              {formData.services_scope?.branding && Object.entries(formData.services_scope.branding).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!value}
                        onChange={handleCheckboxChange}
                        name={`services_scope.branding.${key}`}
                      />
                    }
                    label={getServiceLabel(key)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Events Services */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
              Events
            </Typography>
            <Grid container spacing={2}>
              {formData.services_scope?.events && Object.entries(formData.services_scope.events).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!value}
                        onChange={handleCheckboxChange}
                        name={`services_scope.events.${key}`}
                      />
                    }
                    label={getServiceLabel(key)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
          
          {errors['services_scope'] && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2">
                {errors['services_scope']}
              </Typography>
            </Grid>
          )}
          
          {/* Email Campaign Thresholds */}
          <Grid item xs={12}>
            <Typography variant="h6">Email Campaign Thresholds</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="MOFU Threshold"
              name="email_campaign_thresholds.mofu"
              value={formData.email_campaign_thresholds?.mofu || 0}
              onChange={handleChange}
              error={!!errors['email_campaign_thresholds.mofu']}
              helperText={errors['email_campaign_thresholds.mofu']}
              InputProps={{ inputProps: { } }}
            />
          </Grid>
          
          {/* Email Campaign Scoring */}
          <Grid item xs={12}>
            <Typography variant="h6">Email Campaign Scoring Weights</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {formData.email_campaign_scoring && Object.entries(formData.email_campaign_scoring).map(([metric, value]) => (
                <Grid item xs={12} sm={6} md={4} key={metric}>
                  <TextField
                    fullWidth
                    type="number"
                    label={metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    name={`email_campaign_scoring.${metric}`}
                    value={value}
                    onChange={handleChange}
                    error={!!errors[`email_campaign_scoring.${metric}`]}
                    helperText={errors[`email_campaign_scoring.${metric}`]}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Campaign Budgets */}
          <Grid item xs={12}>
            <Typography variant="h6">Campaign Budgets</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Email Campaigns Budget"
              name="campaign_budgets.email_campaigns"
              value={formData.campaign_budgets?.email_campaigns || 0}
              onChange={handleChange}
              error={!!errors['campaign_budgets.email_campaigns']}
              helperText={errors['campaign_budgets.email_campaigns'] || 'Monthly budget for email campaigns'}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Telemarketing Budget"
              name="campaign_budgets.telemarketing"
              value={formData.campaign_budgets?.telemarketing || 0}
              onChange={handleChange}
              error={!!errors['campaign_budgets.telemarketing']}
              helperText={errors['campaign_budgets.telemarketing'] || 'Monthly budget for telemarketing'}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="LinkedIn Budget"
              name="campaign_budgets.linkedin"
              value={formData.campaign_budgets?.linkedin || 0}
              onChange={handleChange}
              error={!!errors['campaign_budgets.linkedin']}
              helperText={errors['campaign_budgets.linkedin'] || 'Monthly budget for LinkedIn ads'}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Google Budget"
              name="campaign_budgets.google"
              value={formData.campaign_budgets?.google || 0}
              onChange={handleChange}
              error={!!errors['campaign_budgets.google']}
              helperText={errors['campaign_budgets.google'] || 'Monthly budget for Google ads'}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Meta Budget"
              name="campaign_budgets.meta"
              value={formData.campaign_budgets?.meta || 0}
              onChange={handleChange}
              error={!!errors['campaign_budgets.meta']}
              helperText={errors['campaign_budgets.meta'] || 'Monthly budget for Meta ads'}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Currency"
              name="campaign_budgets.currency"
              value={formData.campaign_budgets?.currency || 'INR'}
              onChange={handleChange}
              error={!!errors['campaign_budgets.currency']}
              helperText={errors['campaign_budgets.currency'] || 'Budget currency'}
            >
              <MenuItem value="INR">INR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
            </TextField>
          </Grid>
          
          {/* Form Fill Custom Fields */}
          <Grid item xs={12}>
            <Typography variant="h6">Form Fill Custom Fields</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12}>
            <FormFillCustomFieldsBuilder
              fields={formData.form_fill_custom_fields || []}
              onChange={(fields) => setFormData(prev => ({ ...prev, form_fill_custom_fields: fields }))}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button 
                variant="outlined" 
                onClick={onCancel}
                sx={{ mr: 1 }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                variant="outlined"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : null}
                sx={{
                  borderColor: 'var(--e-global-color-secondary)',
                  color: 'var(--e-global-color-secondary)',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'var(--e-global-color-secondary)',
                    color: '#fff',
                    borderColor: 'var(--e-global-color-secondary)'
                  }
                }}
              >
                {submitting ? 'Saving...' : client ? 'Update Client' : 'Create Client'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default ClientForm;
